import React from 'react'
import styled from 'styled-components'
import { graphql } from 'gatsby'
import { withTranslation } from 'gatsby-plugin-react-i18next'

import SEO from 'src/components/seo'

import {
  ALink,
  StyledGatsbyLink,
  Container,
  Text,
  PageTitle,
  SimpleListItem,
  PrivacyUnorderedList,
  OverflowContainer,
  SubHeader,
} from '../templates/ShopifyPage/styles'
import TosNav from '../components/TosNav'

const SmsPrivacy = () => (
  <>
    <SEO
      title="SMS Privacy Policy"
      description="The Skylight Frame (Glimpse LLC) SMS Privacy Policy."
      path="/sms-privacy/"
    />
    <Container>
      <TosNav currentRoutePath="/sms-privacy/" />
      <OverflowContainer>
        <PageTitle>Skylight Frame Messaging Service Privacy Policy</PageTitle>
        <br />
        <Text>
          This Messaging Service Privacy Policy explains how Skylight Frame (hereinafter, "Skylight
          Frame," "We," "Us," "Our") collects, uses, and shares personal information about you in
          relation to Our text message marketing program (the "Messaging Service"). This Messaging
          Service Privacy Policy supplements{' '}
          <StyledGatsbyLink to="/privacy-policy">Our Primary Privacy Policy</StyledGatsbyLink>. For
          more details, see the section titled "Primary Privacy Policy" below.
        </Text>
        <Text>
          <b>Changes to the Messaging Service Privacy Policy</b>
          <br />
          We may revise this Messaging Service Privacy Policy from time to time in our sole
          discretion. If there are any material changes to this Messaging Service Privacy Policy, we
          will notify you as required by applicable law. You understand and agree that you will be
          deemed to have accepted the updated Messaging Service Privacy Policy if you continue to
          use the Messaging Service after the new Messaging Service Privacy Policy takes effect.
        </Text>
        <Text>
          <b>Personal Information We Collect</b>
          <br />
          When you sign up for the Messaging Service, We collect personal information such as your
          name, phone number, and email address. When you use the Messaging Service to send or
          receive messages, We collect communications metadata (e.g., the time/date a message was
          sent or received) and the contents of any communications you send or receive via the
          Messaging Service.
        </Text>
        <Text>
          We may also collect information about you using cookies or similar technologies on Our
          website or other digital properties. Cookies are small text files placed on device
          browsers that store preferences and facilitate and enhance your experience. Cookies enable
          personalization of your experience via the Messaging Service (e.g., sending you
          personalized text messages such as shopping cart reminders).
        </Text>
        <Text>
          If you participate in a contest, sweepstakes, research study, or email survey associated
          with the Messaging Service, We will collect basic contact information and any other
          information you choose to provide in connection with these activities. We will also
          collect your personal information if you contact Us with questions about the Messaging
          Service or for customer service.
        </Text>
        <Text>
          <b>Use of Personal Information</b>
          <br />
          We use your information to deliver, analyze, maintain and support the Messaging Service.
          We may also use your information to enhance the Messaging Service features and customize
          and personalize your experiences on the Messaging Service; to identify and prevent
          security risks; to prevent fraudulent, deceptive, and malicious activity including
          harassment and identify theft; and to verify and maintain the safety, quality, and
          accuracy of the Messaging Service. For example, We may use cookies to prefill your contact
          information, and to verify that you are authorized to consent to receive marketing
          messages to your device.
        </Text>
        <Text>
          We may use your personal information to generate aggregated and/or de-identified
          information. Aggregated and/or de-identified information is not personal information and
          may be shared with any third party, including advertisers, promotional partners, and
          sponsors.
        </Text>
        <Text>
          <b>Sharing of Personal Information</b>
          <br />
          We may share your personal information as contemplated in Our primary privacy policy, if
          you consent to Us doing so, as well as in the following circumstances:
        </Text>
        <PrivacyUnorderedList>
          <SimpleListItem>
            <em>Third Parties that Help Provide the Messaging Service.</em> We may share your
            personal information with third parties that help Us provide the Messaging Service
            (including, but not limited to, platform providers, phone companies, and other vendors
            who assist us in the delivery of text messages, fraud prevention, maintaining the
            security of the Messaging Service, and to verify your device and contact information).
          </SimpleListItem>
          <SimpleListItem>
            <em>Legal Requirements, Disclosures to Protect Us or Others.</em>
            We may disclose any information We store associated with you to external parties if We,
            in good faith, believe doing so is required or appropriate to: comply with law
            enforcement or national security requests and legal process, such as a court order or
            subpoena; protect your, Our, or others' rights, property, or safety; enforce Our
            policies or contracts; collect amounts owed to Us; or assist with an investigation or
            prosecution of suspected or actual illegal activity.
          </SimpleListItem>
        </PrivacyUnorderedList>
        <Text>
          <b>Accurate Information</b>
          <br />
          When you complete forms online or otherwise provide Us information in connection with the
          Messaging Service, you agree to provide accurate, complete, and true information. You
          agree not to use a false or misleading name or a name that you are not authorized to use.
          If, in Our sole discretion, We believe that any such information is untrue, inaccurate, or
          incomplete, or you have opted into the Messaging Service for an ulterior purpose, We may
          refuse you access to the Messaging Service and pursue any appropriate legal remedies.
        </Text>
        <Text>
          <b>Choices and Controls</b>
          <br />
          Text messages may be sent via an automatic telephone dialing system. Consent to receive
          automated marketing text messages is not a condition of any purchase. You can opt-out of
          receiving further commercial text messages via the Messaging Service by responding to any
          of Our text messages with any of the following replies: STOP, END, CANCEL, UNSUBSCRIBE, or
          QUIT. For additional opt-out information, please review Our{' '}
          <StyledGatsbyLink to="/tos">Terms of Service</StyledGatsbyLink>.
        </Text>
        <Text>
          <b>Customer Care</b>
          <br />
          If you are experiencing any problems with the Messaging Service, please visit
          https://us.manage.myskylight.com and submit the form with details about your problem or
          your request for support, or email{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.
        </Text>
        <Text>
          <b>Supplemental California Privacy Notice</b>
          <br />
          This Supplemental California Privacy Notice only applies to Our processing of personal
          information via the Messaging Service that is subject to the California Consumer Privacy
          Act of 2018 ("<strong>CCPA</strong>"). The CCPA provides California residents with the
          right to know what categories of personal information We have collected about them and
          whether We have disclosed that personal information for a business purpose (e.g., to a
          service provider) in the preceding twelve months. California residents can find this
          information below:
        </Text>

        <StyledTable>
          <thead>
            <tr>
              <th>
                <b>Category of Personal Information Collected by Us</b>
              </th>
              <th>
                <b>
                  Categories of Third Parties Personal Information is Disclosed to for a Business
                  Purpose
                </b>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <b>Identifiers</b>
              </td>
              <td>&bull; Service providers</td>
            </tr>
            <tr>
              <td>
                <b>Personal information categories listed in Cal. Civ. Code &#167; 1798.80(e)</b>
              </td>
              <td>&bull; Service providers</td>
            </tr>
            <tr>
              <td>
                <b>Commercial information</b>
              </td>
              <td>&bull; Service providers</td>
            </tr>
            <tr>
              <td>
                <b>Internet or other electronic network activity</b>
              </td>
              <td>&bull; Service providers</td>
            </tr>
            <tr>
              <td>
                <b>
                  Inferences drawn from other personal information to create a profile about a
                  consumer
                </b>
              </td>
              <td>&bull; Service providers</td>
            </tr>
          </tbody>
        </StyledTable>

        <Text>
          The categories of sources from which We collect personal information and Our business and
          commercial purposes for using personal information are set forth above and in Our primary
          privacy policy.
        </Text>
        <SubHeader>Additional Privacy Rights for California Residents</SubHeader>
        <Text>
          <b>Individual Rights under the CCPA.</b>
          <br />
          The CCPA provides California residents with the right to request:
        </Text>
        <PrivacyUnorderedList>
          <SimpleListItem>
            <b>Access to Personal Information</b>, including obtaining access to or a copy of your
            personal information.
          </SimpleListItem>
          <SimpleListItem>
            <b>Deletion of Personal Information</b>
          </SimpleListItem>
        </PrivacyUnorderedList>
        <Text>
          If you are a California resident and would like to exercise any of your rights under the
          CCPA, please contact Us at{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>. We will process such
          requests in accordance with applicable laws.
        </Text>
        <Text>
          <b>"Sales" of Personal Information under the CCPA.</b> For purposes of the CCPA, unless
          otherwise stated in Our primary privacy policy, We do not "sell" personal information, nor
          do we have actual knowledge of any "sale" of personal information of minors under 16 years
          of age.
        </Text>
        <Text>
          <b>Non-Discrimination.</b> California residents have the right not to receive
          discriminatory treatment by Us for the exercise of their rights conferred by the CCPA.
        </Text>
        <Text>
          <b>Authorized Agent.</b> Only you, or someone legally authorized to act on your behalf,
          may make a verifiable consumer request related to your personal information. To designate
          an authorized agent, please contact Us at{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.
        </Text>
        <Text>
          <b>Verification.</b> When you make a request, We will ask you to provide sufficient
          information that allows Us to reasonably verify you are the person about whom we collected
          personal information or an authorized representative, which may include confirming the
          email address or phone number associated with any personal information We have about you.
        </Text>
        <Text>
          <b>Primary Privacy Policy</b>
          <br />
          By signing up to receive text messages from us, you also agree to{' '}
          <StyledGatsbyLink to="/privacy-policy">Our Primary Privacy Policy</StyledGatsbyLink>. This
          Messaging Service Privacy Policy is strictly limited to the Messaging Service and does not
          limit or restrict any other privacy policy(ies) that may govern the relationship between
          you and Us in other contexts.
        </Text>
      </OverflowContainer>
    </Container>
  </>
)

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #333;

  th,
  td {
    border: 1px solid #333;
    padding: 12px;
    text-align: left;
  }

  th {
    background-color: #f5f5f5;
    font-weight: bold;
  }

  tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  b {
    font-weight: bolder;
    font-family: 'MatterBold';
  }
`

export default withTranslation()(SmsPrivacy)

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["common", "privacy"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
